<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-shop me-2"></i>{{ store.name }} Stock Management
      </h4>
      <!-- Back Button -->
      <div class="d-flex justify-content-center">
        <button class="btn btn-outline-dark" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>

    <!-- Store Products -->
    <StoreProducts ref="storeProducts">
      <template #actions="{ storeProduct }">
        <button class="btn btn-sm btn-outline-info me-1" @click="viewProductHistory(storeProduct)" title="View history">
          <i class="bi bi-clock-history"></i> History
        </button>
        <button class="btn btn-sm btn-outline-primary" @click="populateStoreProduct(storeProduct)">
          <i class="bi bi-pencil"></i> Edit
        </button>
      </template>
    </StoreProducts>
    <!-- End Store Products -->

    <!-- Back Button -->
    <div class="d-flex justify-content-center mb-5">
      <button class="btn btn-outline-dark" @click="$router.go(-1)">
        <i class="bi bi-arrow-left me-1"></i> Back
      </button>
    </div>

    <!-- Edit Store Product Modal -->
    <Modal :open="storeProductModal" @closed="storeProductModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi bi-pencil-square me-2"></i>Edit Store Product
        </h5>
        <button @click="storeProductModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <!-- Product Information Section -->
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-light py-2">
              <h6 class="mb-0"><i class="bi bi-info-circle me-2"></i>Product Information</h6>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="form-group col-md-6">
                  <label for="productName" class="form-label fw-bold">
                    <i class="bi bi-box me-1"></i>Product Name:
                  </label>
                  <input type="text" id="productName" class="form-control" :value="selectedProduct.product.name" disabled>
                </div>
                <div class="form-group col-md-3">
                  <label for="productSku" class="form-label fw-bold">
                    <i class="bi bi-upc me-1"></i>SKU:
                  </label>
                  <input type="text" id="productSku" class="form-control" :value="selectedProduct.product.sku" disabled>
                </div>
                <div class="form-group col-md-3">
                  <label for="productCode" class="form-label fw-bold">
                    <i class="bi bi-code me-1"></i>Code:
                  </label>
                  <input type="text" id="productCode" class="form-control" :value="selectedProduct.product.code" disabled>
                </div>
              </div>
            </div>
          </div>

          <!-- Inventory Section -->
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-light py-2">
              <h6 class="mb-0"><i class="bi bi-stack me-2"></i>Inventory Details</h6>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="form-group col-md-3">
                  <label for="currentStock" class="form-label fw-bold">
                    <i class="bi bi-boxes me-1"></i>Current Stock:
                  </label>
                  <div class="input-group">
                    <input type="number" id="currentStock" class="form-control" v-model="product.stock" :disabled="profile.role !== 'primary'">
                    <span class="input-group-text">{{ selectedProduct.product?.unit?.name }}</span>
                  </div>
                </div>
                <div class="form-group col-md-3">
                  <label for="price" class="form-label fw-bold">
                    <i class="bi bi-dollar-sign me-1"></i>Price:
                  </label>
                  <div class="input-group">
                    <input type="number" id="price" class="form-control" v-model="product.price" :disabled="profile.role !== 'primary'">
                  </div>
                </div>
                <div class="form-group col-md-3">
                  <label for="reorderLevel" class="form-label fw-bold">
                    <i class="bi bi-arrow-repeat me-1"></i>Reorder Level:
                  </label>
                  <div class="input-group">
                    <input type="number" id="reorderLevel" class="form-control" v-model="product.reorder">
                    <span class="input-group-text">{{ selectedProduct.product?.unit?.name }}</span>
                  </div>
                </div>
                <div class="form-group col-md-3">
                  <label for="reorderStatus" class="form-label fw-bold">
                    <i class="bi bi-clipboard-check me-1"></i>Reorder Status:
                  </label>
                  <div class="pt-2">
                    <Badge pill :type="getPillByReorder(selectedProduct.reorder < selectedProduct.stock ? 'good' : 'reorder')">
                      {{ selectedProduct.reorder < selectedProduct.stock ? 'Good' : 'Reorder Needed' }}
                    </Badge>
                  </div>
                </div>
                <div class="form-group col-md-4">
                  <label for="expiryDate" class="form-label fw-bold">
                    <i class="bi bi-calendar-event me-1"></i>Expiry Date:
                  </label>
                  <input type="date" id="expiryDate" class="form-control" v-model="product.expiry_date">
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="storeProductModal = false">
          <i class="bi bi-x-circle me-1"></i>Cancel
        </button>
        <button type="button" class="btn btn-primary" @click="updateStoreProduct">
          <i class="bi bi-check-circle me-1"></i>Update Product
        </button>
      </div>
    </Modal>
    <!-- Edit Store Product Modal End -->

  </Card>
</template>

<script>
import formatNumber from "@/utils/formatNumber";
import {decrypt} from "@/utils/crypto";
import storeStockService from "@/api/storeStockService";
import StoreProducts from "@/components/store/StoreProducts.vue";

export default {
  name: "Stock",
  components: {
    StoreProducts
  },
  data(){
    return {
      profile: {},
      store: {},
      product: {},
      storeProductModal: false,
      selectedProduct: {},
    }
  },

  methods:{
    formatNumber,
    getPillByReorder(cellData) {
      switch (cellData) {
        case 'reorder':
          return 'danger';
        case 'good':
          return 'success';
      }
    },
    async updateStoreProduct() {
      let response = await storeStockService.updateStoreStock(this.product, this.selectedProduct.id)
      if (response) {
        this.storeProductModal = false
        // Refresh the StoreProducts table to reflect latest changes
        await this.$refs.storeProducts?.getStoreStock();
      }
    },
    populateStoreProduct(storeProduct) {
      this.selectedProduct = storeProduct
      if (this.selectedProduct) {
        this.product.reorder = this.selectedProduct.reorder
        this.product.stock = this.selectedProduct.stock
        this.product.price = this.selectedProduct.price
        this.product.expiry_date = this.selectedProduct.expiry_date
        this.storeProductModal = true
      }
    },
    viewProductHistory(storeProduct) {
      const storeId = storeProduct.store_id || this.store?.id || this.$refs.storeProducts?.store?.id;
      const productName = storeProduct.product?.name || storeProduct.product_name || 'Product';
      this.$router.push({
        name: 'product-history',
        params: {
          storeId: storeId,
          productId: storeProduct.product_id,
        },
        query: {
          productName: productName,
        },
      });
    }
  },

  created: async function () {
    const rawProfile = sessionStorage.getItem('profile');
    if (rawProfile) {
      this.profile = JSON.parse(decrypt(rawProfile));
    }
    const rawStore = sessionStorage.getItem('store');
    if (rawStore) {
      this.store = JSON.parse(decrypt(rawStore));
    }
  },

}
</script>

<style scoped>
/* Stock page styles */
.stock-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.product-row {
  transition: all 0.2s ease;
}

.product-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
